import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';

const APP_URL = 'https://app.hanwenchinese.com';

const PublicHeader: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const textColor = scrolled ? '#1a1a2e' : '#fff';

  const linkStyle = (active: boolean): React.CSSProperties => ({
    color: textColor,
    fontWeight: 500,
    cursor: 'pointer',
    fontSize: 19.5,
    fontFamily: "'Space Grotesk', sans-serif",
    textDecoration: 'none',
    opacity: active ? 1 : 0.7,
    borderBottom: active ? `2px solid ${textColor}` : '2px solid transparent',
    paddingBottom: 4,
    transition: 'opacity 0.2s, border-color 0.2s',
  });

  const navItems = [
    { label: t('landing.nav.home'), action: () => navigate('/'), active: isActive('/') },
    { label: t('landing.nav.timetable'), action: () => navigate('/timetable'), active: isActive('/timetable') },
    { label: t('landing.nav.faq'), action: () => navigate('/faq'), active: isActive('/faq') },
  ];

  return (
    <header
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'transparent',
        padding: '0 4vw',
        height: 158,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {/* Logo */}
      <div
        onClick={() => navigate('/')}
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
      >
        <img
          src="/images/landing/logo.png"
          alt="Hanwen Chinese"
          style={{ height: 100 }}
        />
      </div>

      {/* Desktop Nav */}
      <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
        {/* Internal navigation links */}
        <span style={linkStyle(isActive('/'))} onClick={() => navigate('/')}>
          {t('landing.nav.home')}
        </span>
        <span style={linkStyle(isActive('/timetable'))} onClick={() => navigate('/timetable')}>
          {t('landing.nav.timetable')}
        </span>
        <span style={linkStyle(isActive('/faq'))} onClick={() => navigate('/faq')}>
          {t('landing.nav.faq')}
        </span>

        {/* Manage pill button — visually distinct CTA */}
        <a
          href={APP_URL}
          className="manage-nav-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 24px',
            height: 42,
            background: '#03a5da',
            color: '#fff',
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 15,
            fontWeight: 500,
            textDecoration: 'none',
            border: 'none',
            borderRadius: 300,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            marginLeft: 40,
            transition: 'background 0.25s ease, transform 0.2s ease',
          }}
        >
          {t('landing.nav.manage')}
        </a>
      </nav>

      {/* Mobile hamburger */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="mobile-nav-btn"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 8,
        }}
      >
        <MenuOutlined style={{ fontSize: 22, color: textColor }} />
      </button>

      {/* Mobile Drawer */}
      <Drawer
        title={
          <img src="/images/landing/logo.png" alt="Logo" style={{ height: 40 }} />
        }
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {navItems.map((item, i) => (
            <span
              key={i}
              onClick={() => { setDrawerOpen(false); item.action(); }}
              style={{
                display: 'block',
                padding: '14px 0',
                fontSize: 17,
                color: item.active ? '#03a5da' : '#1a1a2e',
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: item.active ? 600 : 500,
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
              }}
            >
              {item.label}
            </span>
          ))}
          <div style={{ marginTop: 24 }}>
            <a
              href={APP_URL}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '14px',
                background: '#03a5da',
                color: '#fff',
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 17,
                fontWeight: 500,
                textDecoration: 'none',
                borderRadius: 300,
              }}
            >
              {t('landing.nav.manage')}
            </a>
          </div>
        </div>
      </Drawer>

      <style>{`
        .desktop-nav { display: flex; align-items: center; }
        .mobile-nav-btn { display: none; }
        .manage-nav-btn:hover {
          background: #0294c4 !important;
          transform: translateY(-1px);
        }
        @media (max-width: 767px) {
          .desktop-nav { display: none !important; }
          .mobile-nav-btn { display: inline-flex; }
        }
      `}</style>
    </header>
  );
};

export default PublicHeader;
